/**
 * Store: Gift with purchase
 * -----------------------------------------------------------------------------
 * Section settings so they're globally available.
 *
 * States:
 * - `active` - Promotions whose conditions have been met.
 * - `added` - Active promotions that have been added to the cart.
 * - `available` - Active promotions that haven't been added to the cart.
 * - `inactive` - Promotions in cart that aren't active and should be removed.
 *
 */
/* eslint-disable no-underscore-dangle */
export default {
  namespaced: true,

  state: () => ({
    active: [],
    added: [],
    available: [],
    dismissed: [],
    id: '',
    inactive: [],
    newAvailable: false,
    promotions: [],
    settings: {
      enable: false,
      excludedProductTypes: [],
      minimumPrice: 0,
      priceRounding: true,
      cta: {
        text: '',
        url: '',
      },
    },
  }),

  getters: {

    /**
     * Get cart that only returns products that are not excluded or gifts.
     * - Also returns updated cart total and items count.
     * @returns {Object}
     */
    getApplicableCart(state, _, rootState) {
      const items = []
      let totalPrice = 0

      rootState.cart.response.items.forEach((item) => {
        const applicable =
          !item.properties?.__gift &&
          !state.settings.excludedProductTypes.includes(item.product_type)

        if (!applicable) {
          return
        }

        totalPrice += item.final_line_price
        items.push(item)
      })

      return {
        item_count: items.length,
        items,
        total_price: totalPrice,
      }
    },

    /**
     * Get if the current promotion ID has been previously dismissed.
     * @returns {Boolean}
     */
    getIdDismissed(state) {
      return state.dismissed.includes(state.id)
    },

    /**
     * Get products with assigned gift products.
     * - Handle object `isFree` is true if gift product is free.
     * @returns {Object}
     */
    getProductsWithGift(state) {
      const products = {}

      state.promotions.forEach((promotion) => {
        if (promotion.trigger.type !== 'product') {
          return
        }

        products[promotion.trigger.value] = {
          isFree:
            promotion.action.discount === 100 && !state.settings.minimumPrice,
        }
      })

      return products
    },

    /**
     * Get threshold values.
     * @returns {Array}
     */
    getThresholds(state) {
      const thresholds = []

      state.promotions.forEach((promotion) => {
        if (promotion.trigger.type !== 'threshold') {
          return
        }

        thresholds.push(promotion.trigger.value)
      })

      return thresholds
    },
  },

  mutations: {

    /**
     * Set `active` promotions.
     * @param {Array} active - Promotions whose conditions have been met.
     */
    SET_ACTIVE(state, active) {
      state.active = active
    },

    /**
     * Set `added` promotions.
     * @param {Array} added - Active promotions that have been added to cart.
     */
    SET_ADDED(state, added) {
      state.added = added
    },

    /**
     * Set `available` promotions.
     * @param {Array} available - Active promotions that aren't in cart.
     */
    SET_AVAILABLE(state, available) {
      state.available = available
    },

    /**
     * Set dismissed.
     * @param {String} id - ID to set as dismissed.
     */
    SET_DISMISSED(state, id) {
      state.dismissed.push(id)
    },

    /**
     * Set unique ID for current available promotions.
     * @param {String} id - Current promotions ID.
     */
    SET_ID(state, id) {
      state.id = id
    },

    /**
     * Set `inactive`.
     * @param {Array} inactive - Promotions in the cart that aren't active and
     * should be removed.
     */
    SET_INACTIVE(state, inactive) {
      state.inactive = inactive
    },

    /**
     * Set promotions.
     * @param {Array} promotions - All promotions.
     */
    SET_PROMOTIONS(state, promotions) {
      state.promotions = promotions
    },

    /**
     * Set settings.
     * @param {Object} settings - Gift with purchase settings.
     */
    SET_SETTINGS(state, settings) {
      state.settings = settings
    },
  },

  actions: {

    /**
     * Set dismissed.
     */
    setDismissed({ commit, state }) {
      commit('SET_DISMISSED', state.id)
    },

    /**
     * Set unique ID for current available promotions.
     * - Means the dismissed state will only apply if the available promotions
     *   haven't changed.
     */
    setId({ commit, state }) {
      const id = state.available.length
        ? state.available.map((promotion) => promotion.id).join('--')
        : 'gift-empty-modal'

      commit('SET_ID', id)
    },

    /**
     * Set promotions.
     * @param {Object} payload - Payload data.
     * @param {Array} payload.blocks - Section blocks passed to Vue.
     * @param {Object} payload.settings - Section settings passed to Vue.
     */
    setPromotions({ commit }, { blocks, settings }) {
      const promotions = blocks
        .map((block, index) => {
          if (!block.trigger) {
            return false
          }

          /**
           * Grab next block for action.
           */
          const action = blocks[index + 1]
          const actionArr = []

          for (const iterator of blocks.slice(index + 1)) {
            if (iterator.trigger) {
              break
            }

            actionArr.push(iterator)
          }

          const actionObj = {
            discount: actionArr.map((item) => item.discount).join('|'),
            type: actionArr.map((item) => item.type).join('|'),
            value: actionArr.map((item) => item[item.type]).join('|'),
          }

          /**
           * Build name specific data.
           */
          const triggerObject = {
            type: block.type,
            value: block[block.type],
          }

          switch (block.type) {
            case 'collection':
              triggerObject.quantity = block.quantity
              break

            case 'threshold':
              triggerObject.value = {}

              block[block.type].split(', ').forEach((string) => {
                const [key, value] = string.split(':')
                triggerObject.value[key] = Number(value.trim())
              })
              break
          }

          /**
           * Return promotion object.
           */
          return {
            // action: {
            //   discount: action.discount,
            //   type: action.type,
            //   value: action[action.type],
            // },
            action: actionObj,
            id: `${block.type}--${block.id}--${action[action.type]}--${
              action.discount
            }`,
            trigger: triggerObject,
          }
        })
        .filter(Boolean)

      commit('SET_SETTINGS', settings)
      commit('SET_PROMOTIONS', promotions)
    },

    /**
     * Set promotion states.
     * - Goes through each promotion and determines its state based on cart.
     * - Non-active is used to keep the opposites of `active`, `inactive` is
     *   only for promotions which are in the cart when they shouldn't be.
     */
    setStates({ commit, dispatch, getters, rootState, state }) {
      const active = []
      const added = []
      const available = []
      const nonActive = []

      const cartItems = rootState.cart.response.items
      const applicableCartItems = getters.getApplicableCart.items
      const applicableCartTotal = getters.getApplicableCart.total_price

      /**
       * Find `active` or non-active promotions.
       * - Collection: find total quantity of items in trigger collection.
       * - Product: find if trigger product is in cart and not a gift.
       * - Threshold: if cart total is above trigger threshold.
       */
      state.promotions.forEach((promotion) => {
        let conditionsMet = false
        let quantity = 0

        switch (promotion.trigger.type) {
          case 'collection':
            applicableCartItems.forEach((item) => {
              if (item.collections && !item.collections.includes(promotion.trigger.value)) {
                return
              }

              quantity += item.quantity
            })

            conditionsMet = quantity >= promotion.trigger.quantity
            break

          case 'product':
            conditionsMet = applicableCartItems.find((item) => {
              return (
                item.handle === promotion.trigger.value &&
                !item.properties?.__gift
              )
            })
            break

          case 'threshold':
            conditionsMet =
              applicableCartTotal >=
              promotion.trigger.value[cnvs.store.currency]
            break
        }

        if (conditionsMet) {
          active.push(promotion)
          return
        }

        nonActive.push(promotion)
      })

      /**
       * Go through active and determine if they're `added` or `available`.
       */
      active.forEach((promotion) => {
        const inCart = cartItems.find((item) => {
          return item.properties?.__gift === promotion.id
        })

        if (inCart) {
          added.push(promotion)
          return
        }

        available.push(promotion)
      })

      /**
       * Go through cart items.
       * - If they have a promotion ID which matches a non-active promotion then
       *   add to inactive for removal.
       */
      const inactive = nonActive
        .map((promotion) => {
          const inCart = cartItems.find((item) => {
            return item.properties?.__gift === promotion.id
          })

          if (!inCart) {
            return false
          }

          return promotion
        })
        .filter(Boolean)

      /**
       * Commit to states.
       */
      commit('SET_ACTIVE', added)
      commit('SET_ADDED', active)
      commit('SET_AVAILABLE', available)
      commit('SET_INACTIVE', inactive)

      /**
       * Dispatch actions.
       */
      dispatch('setId')
    },
  },
}
